import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    Search, BookOpen, Briefcase, MessageSquare, Sparkles, TrendingUp, Clock,
    LifeBuoy, Compass, ClipboardCheck, ShieldCheck, HelpCircle, ArrowRight,
    Star, IdCard, FileText, Users, Zap, GraduationCap, CheckCircle2, PlayCircle,
    Loader2,
} from "lucide-react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import api from "@/lib/api";

/* ------------------------------- Blog ---------------------------------- */

// Icon name on the DB → lucide component for the list card. Admin picks one
// of these when creating a post; anything unknown falls back to BookOpen so
// a bad save never breaks the public page.
const ICONS = {
    BookOpen, Briefcase, MessageSquare, Sparkles, TrendingUp, Users, Zap,
    IdCard, FileText, GraduationCap, PlayCircle, ClipboardCheck, ShieldCheck,
    LifeBuoy, Compass, HelpCircle, Star,
};
const resolveIcon = (name) => ICONS[name] || BookOpen;

const BlogSection = () => {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState(["All"]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [tag, setTag] = useState("All");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [posts, cats] = await Promise.all([
                    api.get("/blog"),
                    api.get("/blog/categories"),
                ]);
                if (cancelled) return;
                setPosts(posts.data.posts || []);
                setCategories(["All", ...(cats.data.categories || [])]);
            } catch (_err) {
                if (!cancelled) {
                    setPosts([]);
                    setCategories(["All"]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() =>
        posts.filter((a) => {
            const matchesTag = tag === "All" || a.category === tag;
            const matchesQ = !q || (a.title + a.excerpt + a.category).toLowerCase().includes(q.toLowerCase());
            return matchesTag && matchesQ;
        }),
        [posts, q, tag]
    );
    return (
        <section id="blog" data-testid="resources-blog" className="scroll-mt-24">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
                <div>
                    <p className="overline">The Blog</p>
                    <h2 className="mt-2 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">Practical career + hiring reads.</h2>
                </div>
                <div className="relative w-full sm:w-72">
                    <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search articles…"
                        data-testid="blog-search"
                        className="pl-10 h-11 bg-card"
                    />
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
                {categories.map((t) => (
                    <button
                        key={t}
                        onClick={() => setTag(t)}
                        data-testid={`blog-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                            tag === t
                                ? "bg-brand-navy text-white border-brand-navy dark:bg-slate-800"
                                : "bg-card text-foreground border-border hover:border-brand-navy"
                        }`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="card-soft p-10 text-center text-muted-foreground flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Loading articles…
                </div>
            ) : filtered.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <h3 className="font-heading font-semibold text-xl text-foreground">No articles match that yet.</h3>
                    <p className="text-muted-foreground mt-2 text-sm">Try a different keyword or browse all tags.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((a, i) => {
                        const Icon = resolveIcon(a.icon_name);
                        return (
                            <motion.div
                                key={a.id || a.slug}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.35, delay: i * 0.02 }}
                                data-testid="blog-card"
                            >
                                <Link
                                    to={`/blog/${a.slug}`}
                                    data-testid={`blog-card-link-${a.slug}`}
                                    className="card-soft p-6 flex flex-col h-full hover:border-emerald-500/40 hover:-translate-y-0.5 transition-all"
                                >
                                    {a.cover_image_url ? (
                                        <img
                                            src={a.cover_image_url}
                                            alt=""
                                            aria-hidden="true"
                                            className="mb-4 rounded-lg h-36 w-full object-cover border border-border"
                                        />
                                    ) : null}
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-accent text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                                            <Icon size={18} />
                                        </div>
                                        <Badge variant="outline" className="text-[11px] text-muted-foreground">{a.category}</Badge>
                                    </div>
                                    <h3 className="mt-5 font-heading font-semibold text-lg text-foreground leading-snug">{a.title}</h3>
                                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed flex-1">{a.excerpt}</p>
                                    <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                                        <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Clock size={12} /> {a.reading_time || "5 min read"}</span>
                                        <span className="text-sm font-semibold text-brand-green dark:text-emerald-400">Read article →</span>
                                    </div>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

/* ---------------------------- Help Center ------------------------------ */

const HELP_TOPICS = [
    { icon: IdCard, title: "Getting Started with Talent Passport™", blurb: "Build, verify, and publish your Simonara profile in under 15 minutes.", articles: 8 },
    { icon: Briefcase, title: "For Employers", blurb: "Uploading jobs, understanding AI Hiring Briefs, requesting Talent Passports.", articles: 12 },
    { icon: ShieldCheck, title: "Trust & Verification", blurb: "How verifications are run and how identity/employment checks work.", articles: 6 },
    { icon: Sparkles, title: "AI Tools", blurb: "AI Resume Optimizer, AI Career Coach, AI Match — how they use your data.", articles: 9 },
    { icon: ClipboardCheck, title: "Account & Billing", blurb: "Managing your subscription, invoices, and verification purchases.", articles: 7 },
    { icon: LifeBuoy, title: "Contact Support", blurb: "Reach a human. Response within 1 business day.", articles: null, cta: { label: "Contact us", to: "/contact" } },
];

const HelpCenterSection = () => (
    <section id="help" data-testid="resources-help" className="scroll-mt-24">
        <div className="mb-8">
            <p className="overline">Help Center</p>
            <h2 className="mt-2 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">Find what you need — fast.</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">Answers, guides, and setup walkthroughs for every part of the Simonara platform.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {HELP_TOPICS.map((t) => (
                <div key={t.title} data-testid="help-topic" className="card-soft p-6 flex flex-col group hover:border-emerald-500/40 transition-colors">
                    <div className="w-11 h-11 rounded-xl bg-accent text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                        <t.icon size={20} />
                    </div>
                    <h3 className="mt-4 font-heading font-semibold text-lg text-foreground">{t.title}</h3>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed flex-1">{t.blurb}</p>
                    {t.cta ? (
                        <Link to={t.cta.to} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-green dark:text-emerald-400 hover:underline">
                            {t.cta.label} <ArrowRight size={13} />
                        </Link>
                    ) : (
                        <div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
                            <span>{t.articles} articles</span>
                            <span className="text-brand-green dark:text-emerald-400 font-semibold group-hover:underline">Browse →</span>
                        </div>
                    )}
                </div>
            ))}
        </div>
    </section>
);

/* -------------------------------- FAQs --------------------------------- */

const FAQS = {
    general: [
        { q: "What is Simonara?", a: "Simonara is an AI-powered talent platform. Employers upload jobs and get an executive-ready AI Hiring Brief™ with their best matched Talent Passports from our Talent Passport™ database. Professionals build one verified profile and get discovered across every Simonara employer." },
        { q: "Is Simonara a job board?", a: "No. We're the opposite of a job board. Instead of posting a role publicly and drowning in applications, employers get 3–5 pre-matched, verified Talent Passports per role. Instead of applying to hundreds of jobs, professionals build one Talent Passport that's automatically matched to relevant opportunities." },
        { q: "How does AI Match™ work?", a: "AI Match™ compares each candidate against your role's listed requirements — skills, experience, location, work type — and shows exactly which requirements each candidate meets and which are missing. No scores, no black-box ranking." },
        { q: "Is my data safe?", a: "Yes. Talent Passports are only shared with employers you approve or when you actively apply. Verification data is processed by our providers under SOC 2 controls and never sold." },
    ],
    talent: [
        { q: "How much does Talent Passport™ cost?", a: "The core Talent Passport is free. Pro ($9.99/mo or $89/yr) unlocks the AI Resume Optimizer, AI Career Coach, priority matching, and unlimited job matches." },
        { q: "How long does verification take?", a: "Identity: minutes. Employment and reference verification: 2–5 business days depending on the employer's HR response time." },
        { q: "Can I stay anonymous until I'm interested in a role?", a: "Yes. Your Talent Passport is discoverable by AI Match, but your contact info is only revealed when you accept an interview request or apply to a specific job." },
        { q: "Does the AI Resume Optimizer replace my resume?", a: "No — it improves it. Personalized suggestions, ATS insights, and role-specific rewrites. You stay in control of every edit." },
    ],
    employer: [
        { q: "What's included in an AI Hiring Brief™?", a: "For every job you upload, you get: a parsed job profile, five matched candidates with reasons + gaps per requirement, and a short executive summary. No scores, no black-box ranking." },
        { q: "How is pricing structured?", a: "Starter ($99/mo, 2 active jobs, 5 Talent Passport™ matches per job) and Growth ($249/mo, unlimited jobs & AI Hiring Briefs™, 25 Talent Passport™ matches per job, 4 team members). Both plans include a 14-day free trial — credit card required to start, your card is charged on day 15 unless you cancel before then, and you can cancel anytime in your account with no charge if you cancel before day 15." },
        { q: "Can I request the full Talent Passport for a candidate?", a: "Yes — one click from your AI Hiring Brief. A Simonara specialist will follow up with the candidate's full verified profile and coordinate next steps." },
        { q: "Do you offer premium recruiting for hard-to-fill roles?", a: "Yes. Our human recruiters step in for executive search, hard-to-fill positions, and full-cycle hiring — all layered on top of the AI platform." },
    ],
};

const FAQAccordion = ({ items, testIdPrefix }) => (
    <Accordion type="single" collapsible className="w-full">
        {items.map((f, i) => (
            <AccordionItem key={f.q} value={`item-${i}`} className="border-border">
                <AccordionTrigger data-testid={`${testIdPrefix}-q-${i}`} className="text-left font-heading text-foreground hover:no-underline text-base">
                    {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed pt-1">
                    {f.a}
                </AccordionContent>
            </AccordionItem>
        ))}
    </Accordion>
);

const FaqSection = () => (
    <section id="faqs" data-testid="resources-faqs" className="scroll-mt-24">
        <div className="mb-8">
            <p className="overline">FAQs</p>
            <h2 className="mt-2 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">Questions? We have answers.</h2>
        </div>
        <Tabs defaultValue="general" className="w-full">
            <TabsList className="bg-card border border-border">
                <TabsTrigger value="general" data-testid="faq-tab-general">General</TabsTrigger>
                <TabsTrigger value="talent" data-testid="faq-tab-talent">For Talent</TabsTrigger>
                <TabsTrigger value="employer" data-testid="faq-tab-employer">For Employers</TabsTrigger>
            </TabsList>
            <TabsContent value="general" className="mt-6"><FAQAccordion items={FAQS.general} testIdPrefix="faq-general" /></TabsContent>
            <TabsContent value="talent" className="mt-6"><FAQAccordion items={FAQS.talent} testIdPrefix="faq-talent" /></TabsContent>
            <TabsContent value="employer" className="mt-6"><FAQAccordion items={FAQS.employer} testIdPrefix="faq-employer" /></TabsContent>
        </Tabs>
    </section>
);

/* ------------------------- Career Resources ---------------------------- */

const CAREER_RESOURCES = [
    { icon: FileText, title: "Resume Template Library", desc: "10 ATS-friendly templates in Word + PDF, tailored to industry.", tag: "Download" },
    { icon: MessageSquare, title: "Interview Prep Toolkit", desc: "Behavioral question bank + a 24-hour interview-day checklist.", tag: "Guide" },
    { icon: TrendingUp, title: "Salary Negotiation Scripts", desc: "Verbatim scripts our candidates use to negotiate offers 12–24% higher.", tag: "Guide" },
    { icon: GraduationCap, title: "Career Change Playbook", desc: "A 6-step framework for pivoting industries without losing salary.", tag: "Playbook" },
    { icon: PlayCircle, title: "Video Introduction Masterclass", desc: "How to record a 60-second intro that beats a resume every time.", tag: "Video" },
    { icon: ClipboardCheck, title: "First-90-Days Success Plan", desc: "The plan we give every candidate to nail their first quarter.", tag: "Template" },
];

const HIRING_RESOURCES = [
    { icon: FileText, title: "Job Description Templates", desc: "AI-optimized JD templates for 40+ common roles — the ones our AI parses best.", tag: "Download" },
    { icon: ClipboardCheck, title: "Interview Scorecards", desc: "Structured scorecards for consistent, bias-reduced interviews.", tag: "Template" },
    { icon: ShieldCheck, title: "Verification Playbook", desc: "When to use identity, employment, and reference checks — and how to sequence them.", tag: "Playbook" },
    { icon: Users, title: "Hiring Manager Kit", desc: "Everything a first-time hiring manager needs to run a great process.", tag: "Kit" },
    { icon: TrendingUp, title: "Compensation Benchmarks", desc: "2026 salary ranges across our most-hired roles, by region.", tag: "Report" },
    { icon: Zap, title: "AI Hiring Brief™ Sample Pack", desc: "Real anonymized examples from placements at 8 different companies.", tag: "Samples" },
];

const ResourceCard = ({ r, testId }) => {
    const Icon = r.icon;
    return (
        <div data-testid={testId} className="card-soft p-6 flex gap-4 items-start hover:border-emerald-500/40 transition-colors">
            <div className="w-11 h-11 shrink-0 rounded-xl bg-accent text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                <Icon size={20} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold text-foreground">{r.title}</h3>
                    <Badge variant="outline" className="text-[10px]">{r.tag}</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{r.desc}</p>
                <button className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-green dark:text-emerald-400 hover:underline">
                    Access resource <ArrowRight size={12} />
                </button>
            </div>
        </div>
    );
};

const CareerSection = () => (
    <section id="career" data-testid="resources-career" className="scroll-mt-24">
        <div className="mb-8">
            <p className="overline">For Talent</p>
            <h2 className="mt-2 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">Career Resources</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">Templates, scripts, and playbooks — the exact tools our recruiters share with candidates every day.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CAREER_RESOURCES.map((r) => <ResourceCard key={r.title} r={r} testId="career-resource" />)}
        </div>
    </section>
);

const HiringSection = () => (
    <section id="hiring" data-testid="resources-hiring" className="scroll-mt-24">
        <div className="mb-8">
            <p className="overline">For Employers</p>
            <h2 className="mt-2 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">Hiring Resources</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">Templates, scorecards, and playbooks to run modern, fair, and fast hiring processes.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {HIRING_RESOURCES.map((r) => <ResourceCard key={r.title} r={r} testId="hiring-resource" />)}
        </div>
    </section>
);

/* --------------------------- Success Stories --------------------------- */

const STORIES = [
    {
        type: "Talent",
        person: "Maria R.",
        role: "Senior Product Designer",
        company: "Placed at a Series C fintech",
        metric: "3 offers in 11 days",
        quote: "I built my Talent Passport on a Sunday. By Thursday, three employers had requested full access. The AI Career Coach helped me negotiate my final offer up by $22k.",
        icon: IdCard,
    },
    {
        type: "Employer",
        person: "Jamal T.",
        role: "VP of People",
        company: "Growth-stage healthcare startup",
        metric: "Hired 4 senior engineers in 5 weeks",
        quote: "The AI Hiring Brief cut our resume-review time from 8 hours to 20 minutes per role. And every candidate we interviewed was actually qualified — that never happened with our old job board.",
        icon: Briefcase,
    },
    {
        type: "Talent",
        person: "David K.",
        role: "Bilingual Operations Lead",
        company: "Placed at a national logistics company",
        metric: "Hired without applying to a single job",
        quote: "I didn't apply anywhere. The employer found me through AI Match and reached out. Two weeks later I had an offer 30% above my previous role.",
        icon: IdCard,
    },
    {
        type: "Employer",
        person: "Priscilla M.",
        role: "Founder + CEO",
        company: "Boutique creative agency",
        metric: "First 6 hires, zero recruiter fees",
        quote: "Simonara replaced our need for a retained recruiter. The verification checklist meant every candidate we spoke with was already verified — no more chasing references at the finish line.",
        icon: Briefcase,
    },
];

const StoriesSection = () => (
    <section id="stories" data-testid="resources-stories" className="scroll-mt-24">
        <div className="mb-8">
            <p className="overline">Real outcomes</p>
            <h2 className="mt-2 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">Success Stories</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">From both sides of the hire — the people, the roles, the results.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {STORIES.map((s) => {
                const Icon = s.icon;
                return (
                    <motion.article
                        key={s.person + s.role}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.4 }}
                        data-testid="success-story"
                        className="card-soft p-7 flex flex-col"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                                <Icon size={13} /> {s.type}
                            </div>
                            <div className="flex items-center gap-1 text-amber-400">
                                <Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" /><Star size={13} fill="currentColor" />
                            </div>
                        </div>
                        <p className="mt-4 text-foreground leading-relaxed">&ldquo;{s.quote}&rdquo;</p>
                        <div className="mt-5 pt-5 border-t border-border">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="font-heading font-semibold text-foreground text-sm">{s.person}</div>
                                    <div className="text-xs text-muted-foreground">{s.role} · {s.company}</div>
                                </div>
                                <div className="mono-score text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                                    <CheckCircle2 size={13} /> {s.metric}
                                </div>
                            </div>
                        </div>
                    </motion.article>
                );
            })}
        </div>
    </section>
);

/* -------------------------------- Page --------------------------------- */

const JUMP_LINKS = [
    { id: "blog", label: "Blog", icon: BookOpen },
    { id: "help", label: "Help Center", icon: LifeBuoy },
    { id: "faqs", label: "FAQs", icon: HelpCircle },
    { id: "career", label: "Career", icon: Compass },
    { id: "hiring", label: "Hiring", icon: Briefcase },
    { id: "stories", label: "Stories", icon: Star },
];

const Resources = () => {
    const location = useLocation();

    // If URL has a hash, scroll to it after mount.
    useEffect(() => {
        if (location.hash) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
            }
        }
    }, [location.hash]);

    return (
        <div data-testid="resources-page" className="bg-background">
            {/* Hero */}
            <section className="bg-brand-navy dark:bg-slate-900 text-white pt-20 pb-16">
                <div className="container-page max-w-4xl">
                    <p className="overline !text-brand-green">Resources</p>
                    <h1 className="mt-3 font-heading font-medium text-4xl lg:text-6xl tracking-tight leading-[1.05]">
                        Everything you need to <span className="text-brand-green">hire smart</span> — and <span className="text-brand-green">get hired well.</span>
                    </h1>
                    <p className="mt-6 text-white/80 text-lg max-w-2xl leading-relaxed">
                        Real advice from real recruiters, plus templates, guides, FAQs, and success stories from both sides of the hire.
                    </p>
                </div>
            </section>

            {/* Sticky jump nav */}
            <div className="sticky top-20 z-30 glass-nav border-b border-border">
                <div className="container-page py-3 flex items-center gap-2 overflow-x-auto">
                    {JUMP_LINKS.map((j) => (
                        <a
                            key={j.id}
                            href={`#${j.id}`}
                            data-testid={`jump-${j.id}`}
                            className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold text-foreground bg-card border border-border hover:border-brand-navy hover:bg-accent transition-colors"
                        >
                            <j.icon size={13} /> {j.label}
                        </a>
                    ))}
                </div>
            </div>

            <div className="container-page py-20 space-y-24">
                <BlogSection />
                <HelpCenterSection />
                <FaqSection />
                <CareerSection />
                <HiringSection />
                <StoriesSection />
            </div>
        </div>
    );
};

export default Resources;
